import React, { useState, useEffect, useCallback } from 'react';
import {
  Page,
  Layout,
  Card,
  ResourceList,
  ResourceItem,
  Text,
  Badge,
  Button,
  InlineStack,
  BlockStack,
  Modal,
  FormLayout,
  TextField,
  Banner,
  EmptyState,
  Spinner,
  Icon,
  ButtonGroup,
} from '@shopify/polaris';
import {
  PrintMinor,
  RefreshMajor,
  DeleteMajor,
  EditMinor,
  CircleTickMajor,
  StarFilledMinor,
} from '@shopify/polaris-icons';
import { epsonService } from '../services/epsonService';

const STATUS_CONFIG = {
  pending: { label: 'Not Authorized', tone: 'attention' },
  authorized: { label: 'Authorized', tone: 'success' },
  error: { label: 'Error', tone: 'critical' },
};

const PrintersPage = ({ setToastMessage, setToastActive }) => {
  const [printers, setPrinters] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // Add printer modal
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    model: '',
    epson_api_key: '',
    epson_client_id: '',
    epson_client_secret: '',
  });

  // Edit modal
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editPrinterId, setEditPrinterId] = useState(null);
  const [editFormData, setEditFormData] = useState({
    name: '',
    model: '',
    epson_api_key: '',
    epson_client_id: '',
    epson_client_secret: '',
  });

  // Delete confirmation
  const [deleteConfirmId, setDeleteConfirmId] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Loading states for individual printer actions
  const [authorizingId, setAuthorizingId] = useState(null);
  const [refreshingId, setRefreshingId] = useState(null);
  const [settingDefaultId, setSettingDefaultId] = useState(null);

  const showToast = useCallback((message) => {
    setToastMessage?.(message);
    setToastActive?.(true);
    setTimeout(() => setToastActive?.(false), 4000);
  }, [setToastMessage, setToastActive]);

  // Fetch printers
  const fetchPrinters = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await epsonService.getPrinters();
      setPrinters(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPrinters();

    // Check for OAuth callback params in URL
    const params = new URLSearchParams(window.location.search);
    const authorizedId = params.get('authorized');
    const oauthError = params.get('error');

    if (authorizedId) {
      showToast('Printer authorized successfully! 🎉');
      // Clean URL
      window.history.replaceState({}, '', window.location.pathname);
    } else if (oauthError) {
      setError(`Authorization failed: ${oauthError}`);
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, [fetchPrinters, showToast]);

  // Add printer
  const handleAddPrinter = async () => {
    if (!formData.name || !formData.epson_api_key || !formData.epson_client_id || !formData.epson_client_secret) {
      return;
    }

    setIsSubmitting(true);
    try {
      await epsonService.addPrinter(formData);
      showToast('Printer added successfully!');
      setIsAddModalOpen(false);
      setFormData({ name: '', model: '', epson_api_key: '', epson_client_id: '', epson_client_secret: '' });
      fetchPrinters();
    } catch (err) {
      showToast(`Error: ${err.message}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Edit printer
  const handleEditPrinter = async () => {
    if (!editPrinterId) return;

    setIsSubmitting(true);
    try {
      await epsonService.updatePrinter(editPrinterId, editFormData);
      showToast('Printer updated successfully!');
      setIsEditModalOpen(false);
      setEditPrinterId(null);
      fetchPrinters();
    } catch (err) {
      showToast(`Error: ${err.message}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const openEditModal = (printer) => {
    setEditPrinterId(printer._id);
    setEditFormData({
      name: printer.name || '',
      model: printer.model || '',
      epson_api_key: '',
      epson_client_id: '',
      epson_client_secret: '',
    });
    setIsEditModalOpen(true);
  };

  // Delete printer
  const handleDeletePrinter = async () => {
    if (!deleteConfirmId) return;

    setIsDeleting(true);
    try {
      await epsonService.deletePrinter(deleteConfirmId);
      showToast('Printer deleted');
      setDeleteConfirmId(null);
      fetchPrinters();
    } catch (err) {
      showToast(`Error: ${err.message}`);
    } finally {
      setIsDeleting(false);
    }
  };

  // Authorize printer
  const handleAuthorize = async (printerId) => {
    setAuthorizingId(printerId);
    try {
      const url = await epsonService.getAuthorizeUrl(printerId);
      if (url) {
        window.open(url, '_blank');
        showToast('Authorization page opened in new tab. Complete the login to authorize.');
      }
    } catch (err) {
      showToast(`Error: ${err.message}`);
    } finally {
      setAuthorizingId(null);
    }
  };

  // Refresh token
  const handleRefreshToken = async (printerId) => {
    setRefreshingId(printerId);
    try {
      await epsonService.refreshToken(printerId);
      showToast('Token refreshed successfully!');
      fetchPrinters();
    } catch (err) {
      showToast(`Error: ${err.message}`);
    } finally {
      setRefreshingId(null);
    }
  };

  // Set default
  const handleSetDefault = async (printerId) => {
    setSettingDefaultId(printerId);
    try {
      await epsonService.setDefaultPrinter(printerId);
      showToast('Default printer updated');
      fetchPrinters();
    } catch (err) {
      showToast(`Error: ${err.message}`);
    } finally {
      setSettingDefaultId(null);
    }
  };

  // Render a single printer item
  const renderPrinterItem = (printer) => {
    const statusConfig = STATUS_CONFIG[printer.status] || STATUS_CONFIG.error;

    return (
      <ResourceItem id={printer._id}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '16px' }}>
          {/* Left: Printer info */}
          <BlockStack gap="100">
            <InlineStack gap="200" align="center">
              <Text variant="bodyMd" fontWeight="bold">{printer.name}</Text>
              {printer.is_default && (
                <Badge tone="info">
                  <InlineStack gap="100" align="center">
                    <Icon source={StarFilledMinor} />
                    <span>Default</span>
                  </InlineStack>
                </Badge>
              )}
              <Badge tone={statusConfig.tone}>{statusConfig.label}</Badge>
            </InlineStack>

            <Text variant="bodySm" tone="subdued">
              {printer.model ? `Model: ${printer.model}` : 'No model specified'}
              {printer.device_info?.printer_name ? ` • ${printer.device_info.printer_name}` : ''}
            </Text>

            {printer.epson_client_id_masked && (
              <Text variant="bodySm" tone="subdued">
                Client ID: {printer.epson_client_id_masked} • API Key: {printer.epson_api_key_masked}
              </Text>
            )}

            {printer.token_expires_at && (
              <Text variant="bodySm" tone="subdued">
                Token expires: {new Date(printer.token_expires_at).toLocaleString()}
              </Text>
            )}
          </BlockStack>

          {/* Right: Actions */}
          <InlineStack gap="200">
            {printer.status === 'pending' && (
              <Button
                onClick={() => handleAuthorize(printer._id)}
                loading={authorizingId === printer._id}
                variant="primary"
                size="slim"
              >
                Authorize
              </Button>
            )}

            {printer.status === 'authorized' && (
              <>
                {!printer.is_default && (
                  <Button
                    onClick={() => handleSetDefault(printer._id)}
                    loading={settingDefaultId === printer._id}
                    size="slim"
                    icon={StarFilledMinor}
                  >
                    Set Default
                  </Button>
                )}
                <Button
                  onClick={() => handleRefreshToken(printer._id)}
                  loading={refreshingId === printer._id}
                  size="slim"
                  icon={RefreshMajor}
                >
                  Refresh Token
                </Button>
              </>
            )}

            {printer.status === 'error' && (
              <Button
                onClick={() => handleAuthorize(printer._id)}
                loading={authorizingId === printer._id}
                tone="critical"
                size="slim"
              >
                Re-Authorize
              </Button>
            )}

            <ButtonGroup>
              <Button
                onClick={() => openEditModal(printer)}
                size="slim"
                icon={EditMinor}
              />
              <Button
                onClick={() => setDeleteConfirmId(printer._id)}
                size="slim"
                icon={DeleteMajor}
                tone="critical"
              />
            </ButtonGroup>
          </InlineStack>
        </div>
      </ResourceItem>
    );
  };

  return (
    <Page
      title="🖨️ Printers"
      primaryAction={{
        content: 'Add Printer',
        onAction: () => setIsAddModalOpen(true),
      }}
      secondaryActions={[
        {
          content: 'Refresh',
          icon: RefreshMajor,
          onAction: fetchPrinters,
        },
      ]}
    >
      <Layout>
        <Layout.Section>
          {error && (
            <div style={{ marginBottom: '16px' }}>
              <Banner tone="critical" onDismiss={() => setError(null)}>
                <p>{error}</p>
              </Banner>
            </div>
          )}

          <Card padding="0">
            {isLoading ? (
              <div style={{ padding: '40px', textAlign: 'center' }}>
                <Spinner size="large" />
                <div style={{ marginTop: '12px' }}>
                  <Text variant="bodyMd" tone="subdued">Loading printers...</Text>
                </div>
              </div>
            ) : printers.length === 0 ? (
              <EmptyState
                heading="No printers configured"
                image=""
                action={{
                  content: 'Add your first printer',
                  onAction: () => setIsAddModalOpen(true),
                }}
              >
                <p>
                  Add an Epson Connect printer to enable cloud printing from the dashboard.
                  You'll need your Epson API Key, Client ID, and Client Secret from the
                  Epson Connect developer portal.
                </p>
              </EmptyState>
            ) : (
              <ResourceList
                resourceName={{ singular: 'printer', plural: 'printers' }}
                items={printers}
                renderItem={renderPrinterItem}
              />
            )}
          </Card>
        </Layout.Section>
      </Layout>

      {/* Add Printer Modal */}
      <Modal
        open={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        title="Add Printer"
        primaryAction={{
          content: 'Add Printer',
          onAction: handleAddPrinter,
          loading: isSubmitting,
          disabled: !formData.name || !formData.epson_api_key || !formData.epson_client_id || !formData.epson_client_secret,
        }}
        secondaryActions={[{ content: 'Cancel', onAction: () => setIsAddModalOpen(false) }]}
      >
        <Modal.Section>
          <FormLayout>
            <TextField
              label="Printer Name"
              value={formData.name}
              onChange={(val) => setFormData(prev => ({ ...prev, name: val }))}
              placeholder="e.g. Office Printer, WP-C879R"
              autoComplete="off"
            />
            <TextField
              label="Printer Model"
              value={formData.model}
              onChange={(val) => setFormData(prev => ({ ...prev, model: val }))}
              placeholder="e.g. WP-C879R"
              helpText="Optional — for identification only"
              autoComplete="off"
            />
            <TextField
              label="Epson API Key"
              value={formData.epson_api_key}
              onChange={(val) => setFormData(prev => ({ ...prev, epson_api_key: val }))}
              placeholder="Your Epson Connect API Key"
              autoComplete="off"
            />
            <TextField
              label="Epson Client ID"
              value={formData.epson_client_id}
              onChange={(val) => setFormData(prev => ({ ...prev, epson_client_id: val }))}
              placeholder="Your Epson Connect Client ID"
              autoComplete="off"
            />
            <TextField
              label="Epson Client Secret"
              value={formData.epson_client_secret}
              onChange={(val) => setFormData(prev => ({ ...prev, epson_client_secret: val }))}
              placeholder="Your Epson Connect Client Secret"
              type="password"
              autoComplete="off"
            />
            <Banner tone="info">
              <p>
                Get these credentials from the{' '}
                <a href="https://developer.epsonconnect.com" target="_blank" rel="noopener noreferrer">
                  Epson Connect Developer Portal
                </a>
                . After adding the printer, you'll need to click "Authorize" to complete the OAuth setup.
              </p>
            </Banner>
          </FormLayout>
        </Modal.Section>
      </Modal>

      {/* Edit Printer Modal */}
      <Modal
        open={isEditModalOpen}
        onClose={() => { setIsEditModalOpen(false); setEditPrinterId(null); }}
        title="Edit Printer"
        primaryAction={{
          content: 'Save Changes',
          onAction: handleEditPrinter,
          loading: isSubmitting,
        }}
        secondaryActions={[{ content: 'Cancel', onAction: () => { setIsEditModalOpen(false); setEditPrinterId(null); } }]}
      >
        <Modal.Section>
          <FormLayout>
            <TextField
              label="Printer Name"
              value={editFormData.name}
              onChange={(val) => setEditFormData(prev => ({ ...prev, name: val }))}
              autoComplete="off"
            />
            <TextField
              label="Printer Model"
              value={editFormData.model}
              onChange={(val) => setEditFormData(prev => ({ ...prev, model: val }))}
              helpText="Optional"
              autoComplete="off"
            />
            <Banner tone="warning">
              <p>
                Changing credentials will reset the printer's authorization. You'll need to re-authorize after saving.
                Leave fields empty to keep existing credentials.
              </p>
            </Banner>
            <TextField
              label="Epson API Key"
              value={editFormData.epson_api_key}
              onChange={(val) => setEditFormData(prev => ({ ...prev, epson_api_key: val }))}
              placeholder="Leave empty to keep existing"
              autoComplete="off"
            />
            <TextField
              label="Epson Client ID"
              value={editFormData.epson_client_id}
              onChange={(val) => setEditFormData(prev => ({ ...prev, epson_client_id: val }))}
              placeholder="Leave empty to keep existing"
              autoComplete="off"
            />
            <TextField
              label="Epson Client Secret"
              value={editFormData.epson_client_secret}
              onChange={(val) => setEditFormData(prev => ({ ...prev, epson_client_secret: val }))}
              placeholder="Leave empty to keep existing"
              type="password"
              autoComplete="off"
            />
          </FormLayout>
        </Modal.Section>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        open={!!deleteConfirmId}
        onClose={() => setDeleteConfirmId(null)}
        title="Delete Printer"
        primaryAction={{
          content: 'Delete',
          onAction: handleDeletePrinter,
          loading: isDeleting,
          destructive: true,
        }}
        secondaryActions={[{ content: 'Cancel', onAction: () => setDeleteConfirmId(null) }]}
      >
        <Modal.Section>
          <Text variant="bodyMd">
            Are you sure you want to delete this printer? This action cannot be undone.
            Any OAuth tokens will be revoked.
          </Text>
        </Modal.Section>
      </Modal>
    </Page>
  );
};

export default PrintersPage;
